/**
 * Cómo se resuelve el orden de las notas de una lista curable (una categoría, y mañana cualquier
 * bloque automático que admita intervención de la redacción).
 *
 * Hay **tres estados** para una nota, y los tres se guardan en `collection_items`:
 *
 * | Estado | Fila guardada | Qué significa su `position` |
 * |---|---|---|
 * | Automática | no hay fila | nada: la resuelve la fecha de publicación |
 * | Movida a mano | `isPinned = false` | su lugar **relativo** frente a las otras movidas |
 * | Fijada | `isPinned = true` | **su número**, literal |
 *
 * La diferencia entre las dos últimas es la que pidió el cliente y no es un detalle:
 *
 *  - Si están **fijadas** la 1 y la 3, una nota nueva entra en la **2** y la que estaba ahí pasa a
 *    la **4**: saltea la 3 porque está clavada.
 *  - Si **movés** una nota al 1 sin fijarla, una nota nueva pasa a ser la 1 y la que moviste queda
 *    2: conserva su lugar relativo, pero no su número.
 *
 * Vive en los contracts porque la misma regla la usan tres lugares: el panel para mostrar la lista,
 * la API para devolverla y —cuando exista— el sitio público para armar la página. Tres
 * implementaciones de esto serían tres órdenes distintos para la misma categoría.
 */
/**
 * Resuelve la lista final. Ver la explicación de arriba para el porqué de cada paso.
 */
export function resolverOrden({ guardadas, publicadas, tope }) {
    const publicadasSet = new Set(publicadas);
    // Una guardada cuya nota ya no está publicada no ocupa lugar. La fila sigue existiendo en la base
    // hasta el próximo guardado: el panel la muestra aparte para que se pueda soltar el pin.
    const vigentes = guardadas.filter((g) => publicadasSet.has(g.articleId));
    const fijadas = new Map();
    const fijadasOrdenadas = vigentes
        .filter((g) => g.fijada)
        .sort((a, b) => a.position - b.position);
    for (const f of fijadasOrdenadas) {
        // Si dos filas reclaman el mismo número, gana la primera: el servidor valida que no pase, pero
        // resolver no puede depender de eso.
        if (!fijadas.has(f.position))
            fijadas.set(f.position, f.articleId);
    }
    const idsFijados = new Set(fijadas.values());
    const idsGuardados = new Set(vigentes.map((g) => g.articleId));
    /**
     * El flujo: lo que va llenando los lugares que no están clavados. Primero las **nuevas** (nunca
     * se tocaron, y llegan ordenadas por fecha), después las **movidas a mano** en el orden que
     * quedaron. Por eso una nota nueva desplaza a una que movieron: entra antes en la fila.
     */
    const flujo = [
        ...publicadas.filter((id) => !idsGuardados.has(id) && !idsFijados.has(id)),
        ...vigentes
            .filter((g) => !g.fijada && !idsFijados.has(g.articleId))
            .sort((a, b) => a.position - b.position)
            .map((g) => g.articleId),
    ];
    const cuantas = Math.min(tope, vigentes.filter((g) => g.fijada).length + flujo.length);
    const resultado = [];
    let siguiente = 0;
    for (let position = 1; position <= cuantas; position += 1) {
        const fijada = fijadas.get(position);
        if (fijada) {
            resultado.push({ articleId: fijada, fijada: true });
            continue;
        }
        const delFlujo = flujo[siguiente];
        siguiente += 1;
        if (delFlujo)
            resultado.push({ articleId: delFlujo, fijada: false });
    }
    // Fijadas con un número más allá del final de la lista: no se pierden, se acomodan al final.
    const yaPuestas = new Set(resultado.map((r) => r.articleId));
    for (const f of fijadasOrdenadas) {
        if (resultado.length >= tope)
            break;
        if (!yaPuestas.has(f.articleId))
            resultado.push({ articleId: f.articleId, fijada: true });
    }
    return resultado.slice(0, tope);
}
/** Cuántas posiciones muestra y guarda el panel para una categoría. */
export const ORDEN_CATEGORIA_TOPE = 20;
/**
 * Posiciones de la portada que se **guardan**. La 1 es la principal.
 *
 * Cuántas se **dibujan** lo decide la maqueta elegida (ver `maqueta-portada.ts`): son seis en A, una
 * en B y cinco en C. Este número es el tope de las tres, así que cambiar de maqueta no borra lo
 * curado: las posiciones que la maqueta no muestra quedan guardadas donde estaban.
 */
export const PORTADA_POSICIONES = 6;
